import type {
	ApiResponseDataModel,
	ApiResponseModel,
	CreateMediaFolderRequestDto,
	CreateMediaUploadRequestDto,
	MediaAccessDto,
	MediaAssetDto,
	MediaFolderDto,
	MediaListQueryDto,
	MediaListResponseDto,
	MediaMonthDto,
	MediaTagDto,
	MediaUploadTicketDto,
	MediaUsageDto,
	MediaVersionDto,
	ReplaceMediaRequestDto,
	SaveMediaTagRequestDto,
	UpdateMediaFolderRequestDto,
	UpdateMediaRequestDto,
} from '@supply-mover-2-0/shared';
import { ApiService, getApiErrorMessage } from './ApiService';

async function attempt<T>(
	request: () => Promise<ApiResponseDataModel<T>>,
): Promise<ApiResponseDataModel<T>> {
	try {
		return await request();
	} catch (error) {
		return { success: false, error: getApiErrorMessage(error) };
	}
}

/**
 * PD-16339 — the media library API (`/api/media`).
 *
 * Every method resolves to the `{ success, data }` envelope rather than throwing, so a
 * screen can show the server's own reason: an item still in use cannot be deleted, a
 * folder that is not empty, a duplicate tag, a brand the user cannot reach.
 *
 * Sending the file itself (the browser's PUT to the signed address) is not here: it
 * needs upload progress, which is a browser concern (see `useMediaUpload` in web-ui).
 */
// eslint-disable-next-line @typescript-eslint/no-extraneous-class
export class MediaQueryService {
	public static getAccess(): Promise<ApiResponseDataModel<MediaAccessDto>> {
		return attempt(() =>
			ApiService.apiGet<ApiResponseDataModel<MediaAccessDto>>('/media/access'),
		);
	}

	public static list(
		query: MediaListQueryDto,
	): Promise<ApiResponseDataModel<MediaListResponseDto>> {
		return attempt(() =>
			ApiService.apiGet<ApiResponseDataModel<MediaListResponseDto>>('/media', {
				...query,
			}),
		);
	}

	/** The months that have items (the month filter's choices), newest first. */
	public static listMonths(
		deleted = false,
	): Promise<ApiResponseDataModel<Array<MediaMonthDto>>> {
		return attempt(() =>
			ApiService.apiGet<ApiResponseDataModel<Array<MediaMonthDto>>>(
				'/media/months',
				deleted ? { deleted: 'true' } : undefined,
			),
		);
	}

	public static get(id: number): Promise<ApiResponseDataModel<MediaAssetDto>> {
		return attempt(() =>
			ApiService.apiGet<ApiResponseDataModel<MediaAssetDto>>(`/media/${id}`),
		);
	}

	/** Items by public id, for screens that store public ids. Unknown ids are left out. */
	public static getByPublicIds(
		publicIds: ReadonlyArray<string>,
	): Promise<ApiResponseDataModel<Array<MediaAssetDto>>> {
		if (publicIds.length === 0) {
			return Promise.resolve({ success: true, data: [] });
		}
		return attempt(() =>
			ApiService.apiGet<ApiResponseDataModel<Array<MediaAssetDto>>>(
				'/media/by-public-id',
				{ ids: publicIds.join(',') },
			),
		);
	}

	public static createUpload(
		request: CreateMediaUploadRequestDto,
	): Promise<ApiResponseDataModel<MediaUploadTicketDto>> {
		return attempt(() =>
			ApiService.apiPost<ApiResponseDataModel<MediaUploadTicketDto>>(
				'/media/uploads',
				request,
			),
		);
	}

	public static markUploaded(
		id: number,
	): Promise<ApiResponseDataModel<MediaAssetDto>> {
		return attempt(() =>
			ApiService.apiPost<ApiResponseDataModel<MediaAssetDto>>(
				`/media/${id}/uploaded`,
			),
		);
	}

	public static createReplaceUpload(
		id: number,
		request: ReplaceMediaRequestDto,
	): Promise<ApiResponseDataModel<MediaUploadTicketDto>> {
		return attempt(() =>
			ApiService.apiPost<ApiResponseDataModel<MediaUploadTicketDto>>(
				`/media/${id}/replace`,
				request,
			),
		);
	}

	public static markReplaceUploaded(
		id: number,
		fileId: number,
	): Promise<ApiResponseDataModel<MediaAssetDto>> {
		return attempt(() =>
			ApiService.apiPost<ApiResponseDataModel<MediaAssetDto>>(
				`/media/${id}/replace/uploaded`,
				{ fileId },
			),
		);
	}

	public static update(
		id: number,
		request: UpdateMediaRequestDto,
	): Promise<ApiResponseDataModel<MediaAssetDto>> {
		return attempt(() =>
			ApiService.apiPatch<ApiResponseDataModel<MediaAssetDto>>(
				`/media/${id}`,
				request,
			),
		);
	}

	public static async delete(id: number): Promise<ApiResponseModel> {
		try {
			return await ApiService.apiDelete<ApiResponseModel>(`/media/${id}`);
		} catch (error) {
			return { success: false, error: getApiErrorMessage(error) };
		}
	}

	public static restore(
		id: number,
	): Promise<ApiResponseDataModel<MediaAssetDto>> {
		return attempt(() =>
			ApiService.apiPost<ApiResponseDataModel<MediaAssetDto>>(
				`/media/${id}/restore`,
			),
		);
	}

	public static listVersions(
		id: number,
	): Promise<ApiResponseDataModel<Array<MediaVersionDto>>> {
		return attempt(() =>
			ApiService.apiGet<ApiResponseDataModel<Array<MediaVersionDto>>>(
				`/media/${id}/versions`,
			),
		);
	}

	public static restoreVersion(
		id: number,
		versionId: number,
	): Promise<ApiResponseDataModel<MediaAssetDto>> {
		return attempt(() =>
			ApiService.apiPost<ApiResponseDataModel<MediaAssetDto>>(
				`/media/${id}/versions/${versionId}/restore`,
			),
		);
	}

	public static listUsage(
		id: number,
	): Promise<ApiResponseDataModel<Array<MediaUsageDto>>> {
		return attempt(() =>
			ApiService.apiGet<ApiResponseDataModel<Array<MediaUsageDto>>>(
				`/media/${id}/usage`,
			),
		);
	}

	public static listFolders(): Promise<
		ApiResponseDataModel<Array<MediaFolderDto>>
	> {
		return attempt(() =>
			ApiService.apiGet<ApiResponseDataModel<Array<MediaFolderDto>>>(
				'/media/folders',
			),
		);
	}

	public static createFolder(
		request: CreateMediaFolderRequestDto,
	): Promise<ApiResponseDataModel<MediaFolderDto>> {
		return attempt(() =>
			ApiService.apiPost<ApiResponseDataModel<MediaFolderDto>>(
				'/media/folders',
				request,
			),
		);
	}

	public static updateFolder(
		id: number,
		request: UpdateMediaFolderRequestDto,
	): Promise<ApiResponseDataModel<MediaFolderDto>> {
		return attempt(() =>
			ApiService.apiPatch<ApiResponseDataModel<MediaFolderDto>>(
				`/media/folders/${id}`,
				request,
			),
		);
	}

	public static async deleteFolder(id: number): Promise<ApiResponseModel> {
		try {
			return await ApiService.apiDelete<ApiResponseModel>(
				`/media/folders/${id}`,
			);
		} catch (error) {
			return { success: false, error: getApiErrorMessage(error) };
		}
	}

	public static listTags(): Promise<ApiResponseDataModel<Array<MediaTagDto>>> {
		return attempt(() =>
			ApiService.apiGet<ApiResponseDataModel<Array<MediaTagDto>>>(
				'/media/tags',
			),
		);
	}

	public static createTag(
		request: SaveMediaTagRequestDto,
	): Promise<ApiResponseDataModel<MediaTagDto>> {
		return attempt(() =>
			ApiService.apiPost<ApiResponseDataModel<MediaTagDto>>(
				'/media/tags',
				request,
			),
		);
	}

	public static renameTag(
		id: number,
		request: SaveMediaTagRequestDto,
	): Promise<ApiResponseDataModel<MediaTagDto>> {
		return attempt(() =>
			ApiService.apiPatch<ApiResponseDataModel<MediaTagDto>>(
				`/media/tags/${id}`,
				request,
			),
		);
	}

	public static async deleteTag(id: number): Promise<ApiResponseModel> {
		try {
			return await ApiService.apiDelete<ApiResponseModel>(`/media/tags/${id}`);
		} catch (error) {
			return { success: false, error: getApiErrorMessage(error) };
		}
	}
}
